import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface QrCodeProps {
    value: string
    label: string
    className?: string
}

// A scannable code for a link, drawn as a crisp SVG so it prints sharply.
// The encoder is loaded in the browser only, after mount, so it stays out of
// the server render and out of pages that never show a code.
export const QrCode: FC<QrCodeProps> = ({ value, label, className }): ReactNode => {
    const [drawing, setDrawing] = useState<{ size: number; path: string } | null>(null)

    useEffect(() => {
        let active = true
        import('qrcode')
            .then(({ create }) => {
                const { modules } = create(value, { errorCorrectionLevel: 'L' })
                let path = ''
                for (let row = 0; row < modules.size; row++) {
                    for (let col = 0; col < modules.size; col++) {
                        if (modules.get(row, col)) path += `M${col} ${row}h1v1h-1z`
                    }
                }
                if (active) setDrawing({ size: modules.size, path })
            })
            .catch(() => {
                if (active) setDrawing(null)
            })
        return () => {
            active = false
        }
    }, [value])

    if (!drawing) return null

    // A quiet zone of 2 modules on each side, which phone cameras need to find the code.
    const box = drawing.size + 4

    return (
        <svg
            role='img'
            aria-label={label}
            viewBox={`-2 -2 ${box} ${box}`}
            shapeRendering='crispEdges'
            className={cn('block rounded-md bg-white', className)}
        >
            <rect x={-2} y={-2} width={box} height={box} fill='#ffffff' />
            <path d={drawing.path} fill='#111111' />
        </svg>
    )
}

export default QrCode
